import { AsyncPipe, NgClass } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatOption } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { RestrictedElementDirective, SuperAdminRoleFilterDirective } from '@auth';
import { ClientStatusComponent } from '@clients';
import { AppService, Country, COUNTRY_LIST, DEFAULT_COUNTRY, PhoneNumberClass } from '@core';
import { ChildClient, ClientUser, RoleNames, rolesRules, UserRolesTypes, UserStatus } from '@models';
import { Store } from '@ngrx/store';
import { ClientManagementSelectors, ClientUsersActions, UserState } from '@ngrx-store';
import { UserManagementService } from '@services';
import { CountrySelectComponent, InlineInputComponent, NotificationsService } from '@ui';
import { NgxMaskPipe } from 'ngx-mask';
import { Observable } from 'rxjs';

import { PersonalProfileComponent } from '../personal-pofile/personal-profile.component';
import { UserStatusComponent } from '../user-status/user-status.component';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [
    AsyncPipe,
    RestrictedElementDirective,
    SuperAdminRoleFilterDirective,
    MatCardModule,
    MatFormField,
    MatIcon,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    MatSlideToggle,
    MatSuffix,
    NgxMaskPipe,
    NgClass,
    UserStatusComponent,
    MatCheckboxModule,
    PersonalProfileComponent,
    InlineInputComponent,
    CountrySelectComponent,
    ClientStatusComponent,
  ],
  templateUrl: './user-details.component.html',
})
export class UserDetailsComponent extends PhoneNumberClass implements OnChanges {
  readonly DEFAULT_COUNTRY = DEFAULT_COUNTRY;
  readonly UserStatus = UserStatus;
  readonly UserRolesTypes = UserRolesTypes;
  readonly RoleNames = RoleNames;
  readonly roleOptions = rolesRules;
  @Input() user!: ClientUser;
  @Input() editor!: UserState;
  @Input() isMobile: boolean = false;
  resetPasswordLoading = false;
  destroyRef = inject(DestroyRef);
  currentClient$: Observable<ChildClient | undefined>;
  userStatusOption = [
    { title: 'Active', value: 'active' },
    { title: 'Inactive', value: 'inactive' },
  ];

  constructor(
    private store: Store,
    private appService: AppService,
    private notificationService: NotificationsService,
    private userManagementService: UserManagementService,
  ) {
    super();
    this.currentClient$ = this.store.select(ClientManagementSelectors.getCurrentClient);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['user']) {
      this.setMaskByCountryCode(this.user?.phone.countryCode);
    }
  }

  updateCountryCode(country: Country, user: ClientUser) {
    this.setMaskByCountryCode(country.code);
    this.store.dispatch(
      ClientUsersActions.updateUserClient({
        clientUserId: user.id,
        clientUser: {
          phone: {
            countryCode: country.code,
            number: user.phone.number,
            smsOptIn: user.phone.smsOptIn,
            countryNumberCode: country.phoneCode,
          },
        },
      }),
    );
  }

  updatePhoneNumber($event: Record<string, string>, user: ClientUser) {
    this.store.dispatch(
      ClientUsersActions.updateUserClient({
        clientUserId: user.id,
        clientUser: {
          phone: {
            countryCode: user.phone.countryCode,
            smsOptIn: user.phone.smsOptIn,
            number: Object.values($event)[0],
            countryNumberCode:
              COUNTRY_LIST.find(country => country.code === user.phone.countryCode)?.phoneCode ||
              DEFAULT_COUNTRY.phoneCode,
          },
        },
      }),
    );
  }

  updateUserData(fieldData: Record<string, string>, user: ClientUser) {
    this.store.dispatch(ClientUsersActions.updateUserClient({ clientUserId: user.id, clientUser: fieldData }));
  }

  toggleNotification(event: MatSlideToggleChange, type: 'sms' | 'email', user: ClientUser) {
    const notificationsPreferences = {
      emailEnabled: type === 'email' ? event.checked : user.notificationsPreferences.emailEnabled,
      smsEnabled: type === 'sms' ? event.checked : user.notificationsPreferences.smsEnabled,
    };

    this.store.dispatch(
      ClientUsersActions.updateUserClient({
        clientUserId: user.id,
        clientUser: { notificationsPreferences },
      }),
    );
  }

  roleUpdated($event: string, user: ClientUser) {
    this.store.dispatch(
      ClientUsersActions.updateUserClient({
        clientUserId: user.id,
        clientUser: {
          roleId: $event,
        },
      }),
    );
  }

  resetPassword(user: ClientUser) {
    this.resetPasswordLoading = true;
    this.userManagementService
      .resetPassword(this.appService.currentClient, user.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resetPasswordLoading = false;
          this.notificationService.showSuccessMessage('Password was successfully reset');
        },
        error: () => {
          this.resetPasswordLoading = false;
          this.notificationService.showErrorMessage('Reset password error');
        },
      });
  }
}
